import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useMemo } from "react";
import {
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal
} from "@internationalized/date";
import ConsistencyScoreTile from "src/ui/components/ConsistencyScoreTile/ConsistencyScoreTile";
import {
  AppQueryPopupsController,
  AppPopup
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import InDialog from "src/ui/components/InDialog/InDialog";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import translate from "src/lib/translate";
import { taskManagementState, userState } from "src/state/state";
import {
  TaskKey,
  type TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import PharmacyInformationDetail from "../../PharmacyInformationDetail/PharmacyInformationDetail";
import {
  WarningIcon,
  CheckmarkIcon,
  IconTaskListLock,
  IconTaskListArrow,
  IconTaskPapersheetCheckmark
} from "src/constants/icons";
import ConsistencyScoreCubit from "src/state/ConsistencyScoreCubit/ConsistencyScoreCubit";
import { useBloc } from "@blac/react";
import { dateLocalString } from "src/lib/date";
import { KnownGroup, KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import { TaskResponse } from "@9amhealth/openapi";
import { OpenBrowser } from "src/hybrid/components/Browser";
import CollectionItemLink from "src/ui/components/StyledComponents/CollectionItemLink";
import Collection from "src/ui/components/StyledComponents/Collection";
import { PayerId } from "src/constants/payers";
import Translate from "src/ui/components/Translate/Translate";
import { REFILL_ARTICLES_CONFIG } from "src/constants/refillArticles";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  gap: 1rem;
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.5rem;
  border-radius: 2rem;
  align-self: flex-start;
  background-color: var(--color-goldenhour-orange);
  font-weight: 500;
  gap: 0.25rem;

  &[data-reached="true"] {
    background-color: var(--color-midmorning-blue);
  }
`;

const Title = styled.h2`
  color: var(--color-text);
  margin: 0 0 0.5rem;
`;

const Message = styled.p`
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const RefillTaskTile = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--color-white);
  border-radius: 8px;
  box-shadow: var(--light-shadow);
  cursor: pointer;
  transition: transform 0.1s ease;

  &[data-locked="true"] {
    cursor: not-allowed;
  }
`;

const TaskIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  background-color: var(--color-cream-darker);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;

  &[data-locked="true"] {
    svg {
      opacity: 0.5;
    }
  }
`;

const TaskContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TaskTitle = styled.p`
  margin: 0;
  font-weight: 500;
`;

const TaskDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-charcoal-60);
`;

const TaskEndIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  flex-shrink: 0;
`;

const CollectionTitle = styled.p`
  color: var(--color-text);
  margin: 0 0 -2rem;
`;

interface RefillTaskItemProps {
  task: TaskResponseKnown;
  title: string;
  description: string;
  isDisabled?: boolean;
  onClick: () => void;
}

const RefillTaskItem: FC<RefillTaskItemProps> = ({
  task,
  title,
  description,
  isDisabled = false,
  onClick
}) => {
  const isLocked = task.status === TaskResponse.status.LOCKED;
  const isTileDisabled = isDisabled || isLocked;

  return (
    <RefillTaskTile data-locked={isTileDisabled} onClick={onClick}>
      <TaskIcon data-locked={isTileDisabled}>
        <IconTaskPapersheetCheckmark />
      </TaskIcon>
      <TaskContent>
        <TaskTitle>{title}</TaskTitle>
        {description && <TaskDescription>{description}</TaskDescription>}
      </TaskContent>
      <TaskEndIcon>
        {isTileDisabled ? <IconTaskListLock /> : <IconTaskListArrow />}
      </TaskEndIcon>
    </RefillTaskTile>
  );
};

const PrepareRefillDialog: FC<{
  returnUrl: string;
}> = ({ returnUrl }) => {
  const [
    { isGlp1RequirementReached, glp1Requirement },
    { getGlp1RefillTaskDescription }
  ] = useBloc(ConsistencyScoreCubit);

  const navigate = useGoToOrBack();

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, {
      multiBack: true,
      replace: true
    });
  };

  // Find the original refill survey task (not the consolidated one)
  const refillSurveyTask = taskManagementState.state.tasks.find(
    (task: TaskResponseKnown) =>
      task.program === KnownProgram.CARE &&
      task.group === KnownGroup.PRESCRIPTION &&
      task.slug === TaskKey.GLP1_REFILL_SURVEY
  );

  const isRefillSurveyExpired = useMemo(() => {
    const availableTo = refillSurveyTask?.availableTo;

    if (!availableTo) {
      return false;
    }

    try {
      const localNow = now(getLocalTimeZone());
      const availableToLocal = parseAbsoluteToLocal(availableTo);

      return availableToLocal.compare(localNow) <= 0;
    } catch {
      const availableToDate = new Date(availableTo);

      if (Number.isNaN(availableToDate.getTime())) {
        return false;
      }

      return availableToDate.getTime() <= Date.now();
    }
  }, [refillSurveyTask?.availableTo]);
  const isRefillSurveyLocked =
    refillSurveyTask?.status === TaskResponse.status.LOCKED;
  const isRefillSurveyDisabled = isRefillSurveyLocked || isRefillSurveyExpired;

  const isEvernorthUser = userState.partnerId === PayerId.EVERNORTH;

  const articles = useMemo(() => {
    const articleKeys: (keyof typeof REFILL_ARTICLES_CONFIG)[] = [
      "managingSideEffects",
      "understandingGlp1"
    ];

    if (isEvernorthUser) {
      articleKeys.push("consistencyScore");
    }

    return articleKeys.map((key) => {
      const config = REFILL_ARTICLES_CONFIG[key];
      return {
        title: translate(`prepareRefillDialog.articles.${key}.title` as const),
        url: config.url,
        imageSrc: config.imageSrc,
        articleLength: config.readingMinutes
      };
    });
  }, [isEvernorthUser]);

  const handleRefillSurveyClick = () => {
    if (!refillSurveyTask || isRefillSurveyDisabled) {
      return;
    }

    AppQueryPopupsController.openPopup(AppPopup.glp1Refill, {
      additionalParameters: { returnUrl }
    });
  };

  return (
    <InDialog
      title={translate("prepareRefillDialog.title", {
        medicationName:
          refillSurveyTask?.additionalData?.medication_display_name
      })}
      onClose={onClose}
      returnUrl={returnUrl}
      width="min(calc(100% - 2rem), 38rem)"
    >
      <Container>
        <StatusPill
          className="as-caption"
          data-reached={isGlp1RequirementReached ? "true" : "false"}
        >
          {isGlp1RequirementReached ? (
            <>
              <CheckmarkIcon />
              {translate("prepareRefillDialog.statusPill.onTrack")}
            </>
          ) : (
            <>
              <WarningIcon />
              {translate("prepareRefillDialog.statusPill.actionRequired")}
            </>
          )}
        </StatusPill>

        <div>
          <Title className="as-subhead2">
            {translate("prepareRefillDialog.mainTitle")}
          </Title>
          <Message className="as-little color-c-80">
            {translate("prepareRefillDialog.message", {
              score: glp1Requirement?.threshold ?? 0,
              date: refillSurveyTask?.availableTo
                ? dateLocalString(refillSurveyTask.availableTo, {
                    dateStyle: "medium"
                  })
                : ""
            })}
          </Message>
        </div>

        <ConsistencyScoreTile simple />

        {refillSurveyTask && (
          <RefillTaskItem
            task={refillSurveyTask}
            title={translate("prepareRefillDialog.refillSurvey.title")}
            description={getGlp1RefillTaskDescription(refillSurveyTask)}
            isDisabled={isRefillSurveyDisabled}
            onClick={handleRefillSurveyClick}
          />
        )}

        <PharmacyInformationDetail
          compact
          descriptionText={translate(
            "prepareRefillDialog.pharmacy.description"
          )}
        />

        <CollectionTitle className="as-subhead2">
          <Translate msg="prepareRefillDialog.relatedMaterials.title" />
        </CollectionTitle>
        <Collection>
          {articles.map((article) => (
            <CollectionItemLink
              key={article.title}
              reverseLabelOrder
              status={translate("prepareRefillDialog.articles.length", {
                length: article.articleLength
              })}
              label={article.title}
              card={true}
              onClick={() => {
                void OpenBrowser(article.url, {
                  useBaseUrl: false,
                  presentationStyle: "fullscreen"
                });
              }}
              imageAlt={article.title}
              imageSrc={article.imageSrc}
              size="large"
            />
          ))}
        </Collection>
      </Container>
    </InDialog>
  );
};

export default PrepareRefillDialog;
